import type { CSSProperties, ReactNode } from "react";

export function Card({
  children,
  style,
  padding = 20,
  testId,
}: {
  children: ReactNode;
  style?: CSSProperties;
  padding?: number;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "white",
        border: "1px solid var(--cc-gray-200)",
        borderRadius: 12,
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-gray-800)" }}>{children}</h2>
      {action}
    </div>
  );
}

export function Badge({
  children,
  tone = "gray",
}: {
  children: ReactNode;
  tone?: "gray" | "teal" | "green" | "amber" | "red" | "navy";
}) {
  const map: Record<string, { bg: string; fg: string }> = {
    gray: { bg: "var(--cc-gray-100)", fg: "var(--cc-gray-700)" },
    teal: { bg: "var(--cc-teal-bg)", fg: "var(--cc-teal)" },
    green: { bg: "var(--cc-green-bg)", fg: "var(--cc-green)" },
    amber: { bg: "var(--cc-amber-bg)", fg: "var(--cc-amber)" },
    red: { bg: "var(--cc-red-bg)", fg: "var(--cc-red)" },
    navy: { bg: "rgba(26,39,68,0.08)", fg: "var(--cc-navy)" },
  };
  const c = map[tone];
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        padding: "3px 9px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

export function PrimaryButton({
  children,
  onClick,
  type = "button",
  testId,
  size = "md",
  variant = "primary",
  disabled = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  testId?: string;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "outline" | "ghost" | "dark";
  disabled?: boolean;
}) {
  const padMap = { sm: "6px 12px", md: "8px 16px", lg: "10px 20px" };
  const fontMap = { sm: 12, md: 13, lg: 14 };
  const bg = variant === "outline" ? "white" : variant === "ghost" ? "transparent" : variant === "dark" ? "var(--cc-navy)" : "linear-gradient(135deg, var(--cc-navy), var(--cc-teal))";
  const fg = variant === "outline" || variant === "ghost" ? "var(--cc-navy)" : "white";
  const border = variant === "outline" ? "1px solid var(--cc-gray-200)" : "none";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        background: bg,
        color: fg,
        border,
        borderRadius: 8,
        padding: padMap[size],
        fontSize: fontMap[size],
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}
